/**
 * Resolve cadence-related rules from a saved filter into a concrete set of
 * entity ids (lead_id / deal_id) via the `filter_entity_ids_by_cadence` RPC.
 *
 * Called from useLeads/useDeals BEFORE applying the saved-filter query so we
 * can convert virtual cadence fields into a `.in('id', […])` clause.
 */

import { supabase } from "@/integrations/supabase/client";
import {
  normalizeFilterFieldPath,
  isCadenceFilterField,
  CADENCE_FIELD_SEQUENCE_DEAL,
  CADENCE_FIELD_SEQUENCE_LEAD,
  CADENCE_FIELD_STATUS_DEAL,
  CADENCE_FIELD_STATUS_LEAD,
  CADENCE_FIELD_STATE_DEAL,
  CADENCE_FIELD_STATE_LEAD,
} from "./relationalFieldRegistry";
import { splitFilterValue } from "./filters/valueTokens";

export type CadenceKind = "lead" | "deal";

type CadenceField = "sequence_id" | "status" | "state";

type CadenceOperator = "equals" | "not_equals" | "is_empty" | "is_not_empty";

type Rule = {
  field: CadenceField;
  operator: CadenceOperator;
  values: string[];
};

/** Statuses tratados como "está em cadência" para `is_not_empty`. */
const ACTIVE_LIKE_STATUSES = ["active", "paused"];

function parseCsv(raw: unknown): string[] {
  if (raw == null) return [];
  return splitFilterValue(String(raw));
}

function classifyField(rawCampo: string, kind: CadenceKind): CadenceField | null {
  const normalized = normalizeFilterFieldPath(rawCampo);
  if (!isCadenceFilterField(normalized)) return null;
  if (kind === "lead") {
    if (normalized === CADENCE_FIELD_SEQUENCE_LEAD) return "sequence_id";
    if (normalized === CADENCE_FIELD_STATUS_LEAD) return "status";
    if (normalized === CADENCE_FIELD_STATE_LEAD) return "state";
  } else {
    if (normalized === CADENCE_FIELD_SEQUENCE_DEAL) return "sequence_id";
    if (normalized === CADENCE_FIELD_STATUS_DEAL) return "status";
    if (normalized === CADENCE_FIELD_STATE_DEAL) return "state";
  }
  return null;
}

function extractCadenceRules(
  filterData: { grupos?: any[] } | null | undefined,
  kind: CadenceKind
): Rule[] {
  if (!filterData?.grupos?.length) return [];
  const out: Rule[] = [];
  for (const grupo of filterData.grupos) {
    if (grupo.tipo_agrupamento !== "AND") continue;
    const regras: any[] = grupo.regras || grupo.filtro_regras || [];
    for (const regra of regras) {
      const field = classifyField(String(regra.campo_filtrado || ""), kind);
      if (!field) continue;
      const rawOp = String(regra.operador || "");
      let op: CadenceOperator;
      if (rawOp === "is_empty" || rawOp === "is_not_empty") {
        op = rawOp;
      } else if (rawOp === "not_equals") {
        op = "not_equals";
      } else {
        op = "equals";
      }
      const values = parseCsv(regra.valor);
      if ((op === "equals" || op === "not_equals") && values.length === 0) continue;
      out.push({ field, operator: op, values });
    }
  }
  return out;
}

function hasCadenceRules(
  filterData: { grupos?: any[] } | null | undefined,
  kind: CadenceKind
): boolean {
  return extractCadenceRules(filterData, kind).length > 0;
}

async function fetchIdsForRule(
  orgId: string,
  kind: CadenceKind,
  rule: Rule
): Promise<Set<string>> {
  const args: {
    p_org_id: string;
    p_kind: CadenceKind;
    p_sequence_ids: string[] | null;
    p_statuses: string[] | null;
    p_state: string | null;
  } = {
    p_org_id: orgId,
    p_kind: kind,
    p_sequence_ids: null,
    p_statuses: null,
    p_state: null,
  };

  if (rule.operator === "is_empty" || rule.operator === "is_not_empty") {
    // "Inscrito em cadência" = enrollment com status active ou paused.
    // Para os dois operadores buscamos a mesma população; quem decide
    // inclusão vs. exclusão é o caller em resolveCadenceRules.
    args.p_statuses = ACTIVE_LIKE_STATUSES;
  } else if (rule.field === "sequence_id") {
    args.p_sequence_ids = rule.values;
  } else if (rule.field === "status") {
    args.p_statuses = rule.values;
  } else if (rule.field === "state") {
    args.p_state = rule.values[0];
  }

  // Usa a variante _arr (uuid[] em linha única) para contornar o cap de 100
  // linhas do PostgREST em listagens de cadência grandes.
  const { data, error } = await supabase.rpc(
    "filter_entity_ids_by_cadence_arr" as never,
    args as never
  );
  if (error) throw error;
  const ids = (data ?? []) as string[];
  return new Set(ids);
}

export interface CadenceResolution {
  /** When non-null, restrict the main query to these ids. */
  matchIds: string[] | null;
  /** When non-null, exclude these ids from the main query (not_equals rules). */
  excludeIds: string[] | null;
}

/**
 * Resolves all cadence rules in the filter to a concrete include/exclude set.
 * Multiple equals rules are AND-intersected. Multiple not_equals rules are
 * union-excluded.
 */
async function resolveCadenceRules(
  orgId: string,
  kind: CadenceKind,
  rules: Rule[]
): Promise<CadenceResolution> {
  if (rules.length === 0) return { matchIds: null, excludeIds: null };

  let matchIntersection: Set<string> | null = null;
  const excludeUnion = new Set<string>();

  for (const rule of rules) {
    const ids = await fetchIdsForRule(orgId, kind, rule);
    if (rule.operator === "equals" || rule.operator === "is_not_empty") {
      if (matchIntersection === null) {
        matchIntersection = ids;
      } else {
        for (const id of matchIntersection) {
          if (!ids.has(id)) matchIntersection.delete(id);
        }
      }
    } else {
      // not_equals e is_empty: excluir o conjunto retornado
      for (const id of ids) excludeUnion.add(id);
    }
  }

  return {
    matchIds: matchIntersection ? Array.from(matchIntersection) : null,
    excludeIds: excludeUnion.size > 0 ? Array.from(excludeUnion) : null,
  };
}

export async function resolveCadenceFilterFromFilterData(
  orgId: string,
  kind: CadenceKind,
  filterData: { grupos?: any[] } | null | undefined
): Promise<CadenceResolution> {
  const rules = extractCadenceRules(filterData, kind);
  return resolveCadenceRules(orgId, kind, rules);
}
